// dictation — parler au lieu d'écrire.
//
// ── CE QUE ÇA DONNE À L'UTILISATEUR ─────────────────────────────────────────
//
// Il décrit son app à la voix. Demande de l'opérateur, le 2026-10-03 : « i want the users to
// be able to talk instead of writing ».
//
// ── POURQUOI LE NAVIGATEUR ET PAS UN MODÈLE QUI ÉCOUTE ──────────────────────
//
// MESURÉ au catalogue le 2026-10-03 : 44 modèles acceptent de l'audio, les moins chers à
// $0,14/M d'entrée, et aucune transcription dédiée chez notre fournisseur. L'API Web Speech,
// elle, coûte ZÉRO — pas de jeton, pas de clé de plus à garder, pas un octet de la voix de
// nos clients qui traverse nos machines. Elle est à la fois la moins chère et la moins
// engageante. Si un jour elle ne suffit pas, le repli par modèle se branchera derrière la
// même interface : c'est pourquoi rien ici ne nomme le navigateur.
//
// ── CE QUI EST VÉRIFIÉ, ET OÙ ───────────────────────────────────────────────
//
// Tout ce module suit MDN, lu le 2026-10-03, jamais un souvenir :
//   • Using_the_Web_Speech_API, verbatim :
//       const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
//   • SpeechRecognition : `event.results[i][0].transcript`, `event.results[i].isFinal`,
//     `event.resultIndex` ; méthodes `start()`, `stop()`, `abort()` ;
//   • `processLocally` : « speech recognition should be handled ON-DEVICE by the browser
//     rather than sending audio to a server ». Sans lui : « on some browsers, like Chrome,
//     … your audio is sent to a web service for recognition processing » ;
//   • `SpeechRecognition.available({langs, processLocally})` rend « available »,
//     « unavailable », ou autre chose (un paquet de langue à installer) ;
//   • les NEUF codes d'erreur, liste donnée comme EXHAUSTIVE.
//
// ── PUR, PARCE QUE C'EST LA SEULE FAÇON DE L'ÉPROUVER ──────────────────────
//
// platform-ui n'a pas de harnais de composants (chat_width.ts le dit). Toute la logique est
// donc ici, en fonctions pures ; le composant ne fait que brancher des écouteurs et appeler
// `start`/`stop`. Une règle écrite dans le composant ne serait protégée par rien.

/** Quand le navigateur ne dit pas sa langue. Un repli, jamais une langue inventée. */
export const DICTATION_LANG_FALLBACK = "en-US";

/** Ce qui a été dit : ce qui est acquis, et ce qui est encore en train de se dire. */
export type Dictation = {
  /** Définitif — le navigateur ne le reprendra plus. */
  readonly final: string;
  /** Provisoire — remplacé au prochain événement. Montré en gris, pas enregistré. */
  readonly interim: string;
};

export const DICTATION_EMPTY: Dictation = { final: "", interim: "" };

/** La forme minimale d'un événement `result`, telle que MDN la décrit. Déclarée ICI pour que
 *  ce module n'ait besoin d'aucune définition de type du navigateur pour être éprouvé. */
export type ResultEventLike = {
  readonly resultIndex: number;
  readonly results: ArrayLike<ArrayLike<{ readonly transcript?: unknown }> & { readonly isFinal?: boolean }>;
};

/**
 * PURE. Un événement `result` appliqué à ce qui était déjà dit.
 *
 * LA BOUCLE PART DE `resultIndex`, ET C'EST TOUT LE SUJET. `event.results` contient TOUT
 * l'historique à chaque événement ; boucler depuis zéro recopierait chaque phrase déjà
 * acquise, et l'utilisateur verrait son texte se dédoubler. MDN expose `resultIndex`
 * exactement pour ça.
 *
 * Le provisoire est RECONSTRUIT à chaque fois, jamais accumulé : le navigateur renvoie une
 * phrase de plus en plus complète, et l'ajouter donnerait « une / une app / une app de ».
 */
export function applyResult(state: Dictation, event: ResultEventLike): Dictation {
  let final = state.final;
  let interim = "";
  const n = event.results?.length ?? 0;
  for (let i = Math.max(0, event.resultIndex | 0); i < n; i++) {
    const r = event.results[i];
    if (r === null || r === undefined) continue;
    const t = r[0]?.transcript;
    if (typeof t !== "string" || t === "") continue;
    if (r.isFinal) final += t;
    else interim += t;
  }
  return { final, interim };
}

/**
 * PURE. La dictée, jointe à ce qui est déjà dans la boîte.
 *
 * Ce qui est TAPÉ n'est jamais abîmé : une dictée vide rend le texte inchangé, à l'espace
 * près. Quelqu'un qui appuie sur le micro, ne dit rien, et retrouve sa phrase rognée ne
 * réessaie pas.
 */
export function joinDictated(existing: string, dictated: string): string {
  const dit = dictated.trim();
  if (dit === "") return existing;
  if (existing.trim() === "") return dit;
  // Une séparation déjà présente est RESPECTÉE — un retour à la ligne volontaire reste un
  // retour à la ligne, il ne devient pas une espace.
  return /\s$/.test(existing) ? `${existing}${dit}` : `${existing} ${dit}`;
}

/**
 * PURE. Ce qui est NOUVEAU dans le définitif depuis le dernier morceau envoyé à la boîte.
 *
 * POURQUOI DES MORCEAUX ET PAS LE TEXTE ENTIER. Si la dictée réécrivait la boîte à chaque
 * événement, elle écraserait ce que l'utilisateur tape PENDANT qu'il parle — or les deux
 * doivent pouvoir se mêler, c'est tout l'intérêt d'une boîte qui reste modifiable. En
 * n'envoyant que l'ajout, on n'efface jamais rien.
 *
 * Un navigateur a le droit de REVOIR son texte définitif (une correction tardive). Quand le
 * nouveau ne prolonge pas l'ancien, on rend le tout : mieux vaut une phrase répétée, que
 * l'utilisateur voit et corrige, qu'une phrase perdue, qu'il ne voit pas.
 */
export function newFinal(sent: string, current: string): string {
  // Rien de neuf donne la chaîne vide SANS garde séparé : un texte identique commence bien
  // par lui-même, donc la tranche est vide. Un `if (current === sent)` aurait dit la même
  // chose deux fois, et aucun test ne pouvait distinguer les deux.
  return current.startsWith(sent) ? current.slice(sent.length) : current;
}

/** Le constructeur de reconnaissance. Volontairement typé au minimum : ce module n'appelle
 *  rien dessus, il le trouve et le rend. */
export type SpeechCtor = new () => unknown;

/**
 * PURE. Le constructeur offert par cette fenêtre, ou `null`.
 *
 * Le nom PRÉFIXÉ compte autant que le standard : c'est celui de Chrome, donc de la plupart
 * des gens. `null` veut dire « ce navigateur ne sait pas », et le bouton ne doit alors PAS
 * exister — un micro qui ne fait rien est pire que pas de micro.
 */
export function speechRecognitionCtor(
  win: { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown },
): SpeechCtor | null {
  for (const c of [win.SpeechRecognition, win.webkitSpeechRecognition]) {
    // `typeof === "function"` et pas seulement « présent » : un navigateur peut exposer la
    // propriété sans l'implémenter, et `new undefined()` lèverait au clic.
    if (typeof c === "function") return c as SpeechCtor;
  }
  return null;
}

/**
 * Relâcher une reconnaissance : `stop` pour en garder le résultat, `abort` pour tout jeter.
 *
 * POURQUOI CE N'EST PAS TROIS LIGNES DANS LE COMPOSANT. C'est la seule chose qui referme le
 * micro de l'utilisateur. Oubliée au démontage, l'onglet garde l'indicateur d'enregistrement
 * sur une page qu'il a quittée, micro ouvert. Une règle de cette nature doit être éprouvable,
 * et dans ce paquet cela veut dire : une fonction, ici.
 *
 * Elle ne lève JAMAIS. Une reconnaissance déjà close lève sur `stop` dans certains
 * navigateurs, et une exception dans un nettoyage de démontage casse le démontage lui-même.
 * Le booléen dit si l'appel est passé, pour qui veut le savoir.
 */
export function releaseRecognition(
  r: { readonly abort?: unknown; readonly stop?: unknown } | null | undefined,
  how: "abort" | "stop",
): boolean {
  if (r === null || r === undefined) return false;
  const f = how === "abort" ? r.abort : r.stop;
  // PAS de garde `typeof f === "function"` : le `try` le couvre déjà — appeler ce qui n'est
  // pas une fonction lève, et on rend `false` comme pour toute autre panne. Le garde aurait
  // dit la même chose deux fois, et aucun test ne pouvait distinguer les deux.
  try { (f as () => void).call(r); return true; } catch { return false; }
}

/**
 * PURE. La voix peut-elle rester sur la machine ?
 *
 * « available » est la SEULE réponse qui l'autorise. MDN : sans reconnaissance locale
 * disponible, l'API échoue sur `language-not-supported` à moins de repasser en service
 * distant — donc forcer le local sur une autre réponse ne protégerait personne, ça
 * casserait la dictée. Les autres réponses (« unavailable », un paquet à télécharger)
 * signifient « pas maintenant ».
 */
export function shouldProcessLocally(availability: string | undefined): boolean {
  return availability === "available";
}

/**
 * PURE. La langue de dictée, prise du navigateur — jamais devinée.
 *
 * Dicter en anglais à quelqu'un qui écrit en arabe ne produit pas une phrase approximative :
 * ça produit du bruit. D'où un repli explicite plutôt qu'un choix implicite.
 */
export function dictationLang(
  nav: { language?: unknown; languages?: readonly unknown[] },
): string {
  for (const v of [nav.language, nav.languages?.[0]]) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return DICTATION_LANG_FALLBACK;
}

/** Les neuf causes de MDN, chacune avec ce qu'elle veut dire POUR L'UTILISATEUR — ce qui lui
 *  arrive, et quoi en faire. Un message passe-partout pour neuf causes n'aide personne, et
 *  une cause sans message laisse un bouton qui ne fait rien et ne dit rien. */
const NOTES: Readonly<Record<string, string>> = {
  "not-allowed":            "Microphone access was blocked. Allow the microphone for this site, then try again.",
  "service-not-allowed":    "This browser won't allow its speech service here. Try another browser, or type instead.",
  "audio-capture":          "No microphone was found. Check that one is connected and not in use elsewhere.",
  "no-speech":              "Nothing was heard. Tap the microphone and speak a little closer.",
  "network":                "Speech recognition needs the network and it didn't answer. Try again, or type instead.",
  "language-not-supported": "Your language isn't available for dictation in this browser. Type instead.",
  "aborted":                "Dictation stopped.",
  "bad-grammar":            "The browser refused the dictation settings. Type instead — this is on us, not you.",
  "phrases-not-supported":  "This browser doesn't support the dictation hints we sent. Type instead — this is on us, not you.",
};

/** PURE. Ce que l'utilisateur lit quand la dictée ne marche pas. Un code inconnu a quand
 *  même un message : la liste de MDN est exhaustive AUJOURD'HUI, et un navigateur en avance
 *  ne doit pas produire un silence. */
export function dictationErrorNote(code: string): string {
  return NOTES[code] ?? "Dictation stopped unexpectedly. Try again, or type instead.";
}
